/**
 * Leviathan Web - Inventory Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface InventoryItem {
  id: string;
  name: string;
  sku: string;
  category: string;
  taxClass: string | null;
  description: string | null;
  productType: string | null;
  unit: string;
  costPerUnit: number | null;
  pricePerUnit: number | null;
  currentStock: number;
  reorderPoint: number | null;
  reorderQuantity: number | null;
  isActive: boolean;
  isTracked: boolean;
}

interface Stats {
  total: number;
  active: number;
  inactive: number;
  lowStock: number;
  totalStock: number;
}

interface StatCardProps {
  label: string;
  value: string | number;
  status?: 'operational' | 'warning' | 'critical';
}

/**
 * Compact stat card for inventory metrics
 */
function StatCard({ label, value, status }: StatCardProps) {
  const statusColor =
    status === 'operational'
      ? 'bg-success-green'
      : status === 'warning'
        ? 'bg-warning-amber'
        : status === 'critical'
          ? 'bg-error-red'
          : 'bg-steel';

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon relative">
        {status && (
          <div className="absolute top-3 right-3">
            <div
              className={`w-2 h-2 ${statusColor} rounded-full animate-pulse`}
              style={{ boxShadow: '0 0 8px currentColor' }}
            />
          </div>
        )}
        <div className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
          {label}
        </div>
        <div
          className="text-3xl font-mono font-bold text-amber"
          style={{
            textShadow: '0 0 10px rgba(255, 149, 0, 0.6)',
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

/**
 * Get category icon/badge
 */
function getCategoryBadge(category: string): { icon: string; label: string } {
  switch (category) {
    case 'FUEL':
      return { icon: 'FUL', label: 'Fuel' };
    case 'ADDITIVE':
      return { icon: 'ADD', label: 'Additive' };
    case 'LUBRICANT':
      return { icon: 'LUB', label: 'Lubricant' };
    case 'EQUIPMENT':
      return { icon: 'EQP', label: 'Equipment' };
    case 'SERVICE':
      return { icon: 'SVC', label: 'Service' };
    default:
      return { icon: 'OTH', label: 'Other' };
  }
}

/**
 * Get stock status
 */
function getStockStatus(
  item: InventoryItem
): { status: 'LOW' | 'OK' | 'UNTRACKED'; color: string } | null {
  if (!item.isTracked) {
    return { status: 'UNTRACKED', color: 'text-text-tertiary' };
  }
  if (!item.reorderPoint) {
    return { status: 'OK', color: 'text-success-green' };
  }
  if (Number(item.currentStock) <= Number(item.reorderPoint)) {
    return { status: 'LOW', color: 'text-error-red' };
  }
  return { status: 'OK', color: 'text-success-green' };
}

const InventoryPage = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchItems();
  }, [search, categoryFilter, activeFilter]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/stats`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);
      if (activeFilter) params.append('isActive', activeFilter);

      const response = await fetch(`${API_URL}/api/inventory?${params}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setItems(data.items);
      }
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setActiveFilter('');
  };

  return (
    <div className="space-y-6 relative">
      {/* Header with geometric frame */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Inventory
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                Product & Stock Management
              </p>
            </div>
            <Link
              to="/inventory/new"
              className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-6 transition-all uppercase tracking-wider relative group"
              style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
            >
              <span className="relative z-10">+ New Product</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ mixBlendMode: 'overlay' }}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Total Items" value={stats.total} />
          <StatCard label="Active" value={stats.active} status="operational" />
          <StatCard label="Inactive" value={stats.inactive} />
          <StatCard
            label="Low Stock"
            value={stats.lowStock}
            status={stats.lowStock > 0 ? 'warning' : undefined}
          />
          <StatCard label="Total Stock" value={`${stats.totalStock.toLocaleString()} gal`} />
        </div>
      )}

      {/* Filters Panel */}
      <GeometricPanel>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, SKU, description..."
              className="form-input"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Category
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="form-input"
            >
              <option value="">All Categories</option>
              <option value="FUEL">Fuel</option>
              <option value="ADDITIVE">Additive</option>
              <option value="LUBRICANT">Lubricant</option>
              <option value="EQUIPMENT">Equipment</option>
              <option value="SERVICE">Service</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Status
            </label>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="form-input"
            >
              <option value="">All Items</option>
              <option value="true">Active Only</option>
              <option value="false">Inactive Only</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-4 transition-all uppercase text-xs tracking-wider border border-panel-border hover:border-steel"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </GeometricPanel>

      {/* Inventory Table */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border clip-path-octagon overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono animate-pulse">
                &gt; Loading inventory...
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono text-lg mb-2">
                &gt; No inventory items found
              </div>
              <p className="text-text-tertiary font-mono text-sm">
                {search || categoryFilter || activeFilter
                  ? 'Try adjusting your search filters'
                  : 'Add your first product to get started'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background border-b border-panel-border">
                  <tr>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Product
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Category
                    </th>
                    <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Stock
                    </th>
                    <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Reorder Point
                    </th>
                    <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Cost
                    </th>
                    <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Price
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Status
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const category = getCategoryBadge(item.category);
                    const stockStatus = getStockStatus(item);

                    return (
                      <tr
                        key={item.id}
                        className="border-b border-panel-border hover:bg-amber/5 transition-colors cursor-pointer"
                        onClick={() => navigate(`/inventory/${item.id}`)}
                      >
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-amber font-mono font-semibold">
                              {item.name}
                            </div>
                            <div className="text-text-tertiary font-mono text-xs">
                              {item.sku}
                              {item.productType && ` - ${item.productType}`}
                            </div>
                            {/* NO TAX CLASS. Fuel taxes are matched by tax
                                class, not by category, so this product is
                                invoiced with no fuel tax at all — silently.
                                Shown on the list as well as the form because
                                nobody opens every product to check. */}
                            {!item.taxClass && (
                              <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono text-error-red">
                                <AlertTriangle className="w-3 h-3" />
                                No tax class — invoiced untaxed
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-mono bg-panel-border px-2 py-1 text-steel">
                              {category.icon}
                            </span>
                            <span className="text-text-secondary font-mono text-sm">
                              {category.label}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div
                            className="text-amber font-mono font-semibold"
                            style={{ textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' }}
                          >
                            {Number(item.currentStock).toLocaleString()} {item.unit}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="text-text-secondary font-mono text-sm">
                            {item.reorderPoint
                              ? `${Number(item.reorderPoint).toLocaleString()} ${item.unit}`
                              : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="text-text-secondary font-mono text-sm">
                            {item.costPerUnit
                              ? `$${Number(item.costPerUnit).toFixed(4)}`
                              : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div
                            className="text-success-green font-mono font-semibold"
                            style={{ textShadow: '0 0 6px rgba(74, 222, 128, 0.4)' }}
                          >
                            {item.pricePerUnit
                              ? `$${Number(item.pricePerUnit).toFixed(4)}`
                              : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {!item.isActive ? (
                            <span className="text-text-tertiary font-mono text-xs uppercase tracking-wider">
                              INACTIVE
                            </span>
                          ) : stockStatus ? (
                            <span
                              className={`${stockStatus.color} font-mono text-xs uppercase tracking-wider font-semibold`}
                              style={{ textShadow: '0 0 6px currentColor' }}
                            >
                              {stockStatus.status}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => navigate(`/inventory/${item.id}/edit`)}
                              className="text-text-secondary hover:text-amber font-mono text-sm"
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => navigate(`/inventory/${item.id}`)}
                              className="text-steel hover:text-amber font-mono text-sm"
                              title="View"
                            >
                              👁️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryPage;
